"use client";

const capabilities = [
  {
    area: "Executive Leadership",
    description:
      "Fractional and interim CTO, CIO, and CISO engagements. We step into your organization and lead—bridging the gap between technical teams, operations, and the boardroom.",
    tags: ["CTO", "CIO", "CISO", "Board-Level Strategy"],
  },
  {
    area: "Infrastructure & Cloud",
    description:
      "Cloud architecture redesigns, datacenter migrations, and hybrid environments built for production. Deep AWS expertise with hands-on implementation—not just diagrams.",
    tags: ["AWS", "Cloud Migration", "Hybrid Architecture", "IaC"],
  },
  {
    area: "Security & Compliance",
    description:
      "Zero-trust environment design, security program builds, blue team expansion, and physical security modernization. Comprehensive security operations from strategy through execution.",
    tags: ["Zero Trust", "Security Programs", "Compliance", "Physical Security"],
  },
  {
    area: "Teams & Operations",
    description:
      "Engineering departments, SRE teams, and operations groups built from scratch. Hiring, structuring, mentoring, and scaling—across organizations of hundreds.",
    tags: ["Team Building", "SRE", "DevOps", "Incident Management"],
  },
];

export default function TeamSection() {
  return (
    <section id="team" aria-label="About the firm" className="py-20 sm:py-24 md:py-32 bg-[#111114] relative overflow-hidden">
      {/* Background texture */}
      <div aria-hidden="true" className="absolute inset-0 opacity-[0.015]"
      style={{
        backgroundImage: "radial-gradient(circle at 2px 2px, rgba(184,152,106,1) 1px, transparent 0)",
        backgroundSize: "40px 40px"
      }} />

      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-12 relative">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-12 sm:mb-16 md:mb-20 gap-6 sm:gap-8">
          <div>
            <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 reveal-hidden" data-reveal>
              <div className="w-6 sm:w-8 h-[1px] bg-[#B8986A]" />
              <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.3em] sm:tracking-[0.35em] uppercase text-[#B8986A]">
                About the Firm
              </span>
            </div>
            <h2
              className="font-fraunces text-[clamp(2rem,8vw,4.5rem)] sm:text-[clamp(2.5rem,5vw,4.5rem)] font-light leading-[1.05] tracking-tight text-[#F2EFE9] reveal-hidden"
              data-reveal>

              Built by Operators,
              <br />
              <span className="italic text-gradient-gold">Not Advisors.</span>
            </h2>
          </div>
          <p className="text-[#A09D97] text-sm sm:text-base font-light leading-relaxed max-w-md reveal-hidden delay-200" data-reveal>
            JAMO was founded on a simple principle: find the biggest need and go solve it. We bring 20+ years of experience operating at every level—from hands-on infrastructure to the executive suite.
          </p>
        </div>

        {/* What We Bring */}
        <div className="mb-12 sm:mb-16 reveal-hidden" data-reveal>
          <div className="glass-card p-6 sm:p-8 md:p-10">
            <h3 className="font-fraunces text-2xl sm:text-3xl font-light text-[#F2EFE9] tracking-tight mb-2">
              What We Bring
            </h3>
            <p className="text-[#A09D97] text-sm sm:text-base font-light leading-relaxed mb-6 max-w-2xl">
              JAMO operates across the full spectrum of technology leadership. Whether you need someone in the boardroom or in the server room, we scale our involvement to where your organization needs it most.
            </p>

            <div className="flex flex-wrap gap-3">
              {["Executive Leadership", "Cloud & Infrastructure", "Security & Zero Trust", "Team Building", "DevOps & CI/CD", "SRE & Monitoring", "Incident Management", "Vendor Strategy"].map((tag) => (
                <span
                  key={tag}
                  className="text-[9px] font-bold tracking-[0.15em] uppercase text-[#B8986A] border border-[rgba(184,152,106,0.2)] px-3 py-1.5 rounded-sm">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Capability Areas */}
        <div className="space-y-0">
          <div className="flex items-center gap-3 sm:gap-4 mb-8 reveal-hidden" data-reveal>
            <div className="w-6 sm:w-8 h-[1px] bg-[#B8986A]" />
            <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.3em] sm:tracking-[0.35em] uppercase text-[#B8986A]">
              Core Capabilities
            </span>
          </div>

          {capabilities.map((item, i) => (
            <div
              key={item.area}
              className={`grid md:grid-cols-[200px_1fr] gap-4 sm:gap-8 py-8 sm:py-10 border-b border-[rgba(242,239,233,0.06)] last:border-b-0 reveal-hidden delay-${i * 100 + 100}`}
              data-reveal>

              {/* Left — Area */}
              <div>
                <div className="text-[#F2EFE9] text-sm font-semibold">{item.area}</div>
              </div>

              {/* Right — Content */}
              <div>
                <p className="text-[#A09D97] text-sm font-light leading-relaxed mb-4">
                  {item.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[9px] font-bold tracking-[0.15em] uppercase text-[#6B6864] border border-[rgba(242,239,233,0.08)] px-2 py-1 rounded-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="mt-12 sm:mt-16 text-center reveal-hidden" data-reveal>
          <p className="text-[#6B6864] text-sm font-light">
            Need additional expertise? JAMO brings in trusted specialists when the project calls for it.
          </p>
        </div>
      </div>
    </section>);

}
